import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Home, Calendar, Trophy, UtensilsCrossed, Instagram, Award, Handshake, LayoutGrid } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getLogo, getSettings } from '../../lib/api';

const allNavItems = [
  { path: '/', icon: Home, label: 'Home', alwaysVisible: true },
  { path: '/gironi', icon: Trophy, label: 'Gironi', requiresDraw: true },
  { path: '/calendario', icon: Calendar, label: 'Calendario', requiresDraw: true },
  { path: '/tabellone', icon: LayoutGrid, label: 'Tabellone', requiresDraw: true },
  { path: '/sponsor', icon: Handshake, label: 'Sponsor', alwaysVisible: true },
  { path: '/menu', icon: UtensilsCrossed, label: 'Menu', alwaysVisible: true },
];

const useVisibleNavItems = () => {
  const [drawCompleted, setDrawCompleted] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getSettings()
      .then((res) => { if (!cancelled) setDrawCompleted(!!res.data?.draw_completed); })
      .catch(() => { if (!cancelled) setDrawCompleted(false); });
    return () => { cancelled = true; };
  }, []);

  // Until we know, hide the gated items (avoid flash)
  return allNavItems.filter((item) => item.alwaysVisible || (item.requiresDraw && drawCompleted === true));
};

export const MobileNav = () => {
  const location = useLocation();
  const navItems = useVisibleNavItems();

  return (
    <nav className="bottom-nav md:hidden safe-area-bottom" data-testid="mobile-nav">
      {navItems.map((item) => {
        const isActive = location.pathname === item.path;
        return (
          <Link
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={cn(
              'flex flex-col items-center gap-0.5 px-2 py-2 rounded-lg transition-colors min-w-[56px]',
              isActive ? 'text-primary' : 'text-slate-400 hover:text-white'
            )}
          >
            <item.icon className={cn('w-5 h-5', isActive && 'drop-shadow-[0_0_8px_rgba(57,255,20,0.6)]')} />
            <span className="text-[10px] font-medium">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
};

export const DesktopNav = () => {
  const location = useLocation();
  const [logoUrl, setLogoUrl] = useState('/logo192.png');
  const navItems = useVisibleNavItems();

  useEffect(() => {
    getLogo()
      .then(res => {
        if (res.data?.has_logo) {
          setLogoUrl(`data:${res.data.content_type};base64,${res.data.data}`);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-white/5" data-testid="desktop-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 md:h-16">
          <Link to="/" className="flex items-center gap-2 md:gap-3">
            <img 
              src={logoUrl} 
              alt="Logo Torneo" 
              className="w-8 h-8 md:w-10 md:h-10 rounded-full object-contain"
            />
            <div>
              <h1 className="font-heading text-sm md:text-lg font-bold tracking-tight">TORNEO NOTTURNO</h1>
              <div className="flex items-center gap-1">
                <Award className="w-3 h-3 text-gold" />
                <p className="text-[10px] md:text-xs text-gold font-semibold">30ª EDIZIONE - VIADANA</p>
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`desktop-nav-${item.label.toLowerCase()}`}
                  className={cn(
                    'px-4 py-2 font-heading font-semibold uppercase tracking-wide text-sm transition-colors relative',
                    isActive ? 'text-primary tab-active' : 'text-slate-400 hover:text-white'
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <a
            href="https://www.instagram.com/torneo_notturno_viadana"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-slate-400 hover:text-pink-500 transition-colors"
            data-testid="instagram-link"
          >
            <Instagram className="w-5 h-5" />
            <span className="hidden lg:inline text-sm">@torneo_notturno_viadana</span>
          </a>
        </div>
      </div>
    </header>
  );
};

export const MainLayout = ({ children }) => {
  return (
    <div className="min-h-[100svh] noise-overlay">
      <DesktopNav />
      <main className="pb-20 md:pb-0">
        {children}
      </main>
      <MobileNav />
    </div>
  );
};

export default MainLayout;
